import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CLINICAL_TRIAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Novavax COVID-19 Vaccine Study (65+)
 * Target population: Adults 65+ at high risk for severe COVID-19
 */
export const novavaxCovid65ScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49",
        disqualify: true
      },
      {
        value: "50_to_64",
        label: "50 to 64",
        disqualify: true
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 65 and older.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  {
    id: "covid_vaccine_history",
    type: QuestionType.RADIO,
    question: "Have you ever received a COVID-19 vaccine?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received at least one COVID-19 vaccine"
      },
      {
        value: "no",
        label: "No, I have never received a COVID-19 vaccine",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study requires participants to have received at least one COVID-19 vaccine previously.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 21797, booleanAttributeValue: true }, // had_covid_vaccine
      ],
      no: [
        { attributeDefinitionId: 21797, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_covid_vaccine",
    type: QuestionType.RADIO,
    question: "Have you received a COVID-19 vaccine in the past 3 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received a COVID-19 vaccine in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received a COVID-19 vaccine in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received a COVID-19 vaccine in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23730, booleanAttributeValue: true }, // had_covidvaccine_3months
      ],
      no: [
        { attributeDefinitionId: 23730, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_any_vaccine",
    type: QuestionType.RADIO,
    question: "Have you received any vaccine (COVID-19, flu, or other) in the past 2 weeks?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received a vaccine in the past 2 weeks",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received any vaccine in the past 2 weeks"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received any vaccine in the past 2 weeks cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25110, booleanAttributeValue: true }, // had_vaccination_covid_influenza_2_weeks
      ],
      no: [
        { attributeDefinitionId: 25110, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "recent_covid_infection",
    type: QuestionType.RADIO,
    question: "Have you had a confirmed COVID-19 infection in the past 3 months?",
    helpText: "This refers to a positive COVID-19 test (PCR or antigen) in the past 3 months.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had a confirmed COVID-19 infection in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had a confirmed COVID-19 infection in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a COVID-19 infection in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23428, booleanAttributeValue: true }, // had_covid_infection_or_vaccine_3_months
      ],
      no: [
        { attributeDefinitionId: 23428, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "vaccine_anaphylaxis",
    type: QuestionType.RADIO,
    question: "Have you ever had a severe allergic reaction (anaphylaxis) to any vaccine?",
    helpText: "This includes reactions like difficulty breathing, swelling of the throat, or requiring emergency medical care.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have had anaphylaxis to a vaccine",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not had anaphylaxis to a vaccine"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of anaphylaxis to vaccines cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23486, booleanAttributeValue: true }, // had_anaphylaxis_vaccine
      ],
      no: [
        { attributeDefinitionId: 23486, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "heart_inflammation",
    type: QuestionType.RADIO,
    question: "Have you ever been diagnosed with myocarditis (heart muscle inflammation) or pericarditis (heart lining inflammation)?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with myocarditis or pericarditis",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with myocarditis or pericarditis"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with a history of myocarditis or pericarditis cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 25888, booleanAttributeValue: true }, // history_myocarditis_pericarditis_prerequisite
      ],
      no: [
        { attributeDefinitionId: 25888, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "active_cancer",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with or treated for cancer in the past 3 years (excluding non-melanoma skin cancer)?",
    helpText: "Non-melanoma skin cancers such as basal cell or squamous cell carcinoma do not disqualify you.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with or treated for cancer in the past 3 years",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with or treated for cancer in the past 3 years"
      },
      {
        value: "skin_cancer_only",
        label: "Only non-melanoma skin cancer (basal cell or squamous cell carcinoma)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants with active cancer or cancer treatment in the past 3 years cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26687, booleanAttributeValue: true }, // has_active_cancer_3_years
      ],
      no: [
        { attributeDefinitionId: 26687, booleanAttributeValue: false },
      ],
      skin_cancer_only: [
        { attributeDefinitionId: 26687, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "immunosuppressants_3_months",
    type: QuestionType.RADIO,
    question: "Have you taken any immunosuppressive medications in the past 3 months?",
    helpText: "This includes medications like prednisone (high dose), methotrexate, azathioprine, cyclosporine, or rituximab.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have taken immunosuppressive medications in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not taken immunosuppressive medications in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have taken immunosuppressive medications in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
      ],
      no: [
        { attributeDefinitionId: 3052, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "blood_products_3_months",
    type: QuestionType.RADIO,
    question: "Have you received any blood transfusions or blood-related products in the past 3 months?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received blood products in the past 3 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received blood products in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received blood products in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23724, booleanAttributeValue: true }, // had_blood_products_3_months
      ],
      no: [
        { attributeDefinitionId: 23724, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "pregnancy_breastfeeding",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am currently pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I am not currently pregnant or breastfeeding"
      },
      {
        value: "not_applicable",
        label: "Not applicable (I am male or cannot become pregnant)"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who are pregnant or breastfeeding cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26453, booleanAttributeValue: true }, // is_pregnant_prerequisite
        { attributeDefinitionId: 26455, booleanAttributeValue: true }, // is_breastfeeding_prerequisite
      ],
      no: [
        { attributeDefinitionId: 26453, booleanAttributeValue: false },
        { attributeDefinitionId: 26455, booleanAttributeValue: false },
      ],
      not_applicable: [],
    }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently participating in another clinical trial"
      },
      {
        value: "yes",
        label: "Yes, I am currently participating in another clinical trial",
        disqualify: true
      },
    ],
    disqualifyMessage: "For safety reasons, participants currently enrolled in another clinical trial cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
